import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Mail,
  Phone,
  X,
  MapPin,
  Share2,
  Star,
  Package,
  Zap,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import AuthenticatedNavbar from "../components/AuthenticatedNavbar";
import TopNavbar from "../components/TopNavbar";
import dealsService from "../components/services/DealsService";
import Loader from "../components/Loader";
import promotionService from "../components/services/PromotionService";
import ApplicationConfirmationModal from "../components/ApplicationConfirmationModal";
import { useSelector } from "react-redux";

const DealDetails = () => {
  const navigate = useNavigate();
  const { dealSlug } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [showImageLightbox, setShowImageLightbox] = useState(false);
  const [dealData, setDealData] = useState(null);
  const { user } = useSelector((state) => state.auth);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [flashSaleCountdown, setFlashSaleCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchDeal = async () => {
      setIsLoading(true);
      try {
        const response = await dealsService.getDealDetails(dealSlug);
        if (response.data.status_code === "000") {
          setDealData(response.data.data);
          setIsLoading(false);
        } else {
          toast.error(response.data.reason);
        }
      } catch (error) {
        toast.error("Failed to load deal details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeal();
  }, [dealSlug]);

  // Flash Sale Countdown Timer
  useEffect(() => {
    if (
      !dealData ||
      dealData.validity_type !== "flash_sale" ||
      !dealData.flash ||
      dealData.flash.length === 0
    ) {
      return;
    }

    const flashEndTime = moment(dealData.flash[0]?.flash_end_time);

    const updateCountdown = () => {
      const now = moment();
      if (now.isAfter(flashEndTime)) {
        setFlashSaleCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const diff = flashEndTime.diff(now);
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(
        (diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      );
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setFlashSaleCountdown({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [dealData]);

  // Get all images for carousel
  const getAllImages = useMemo(() => {
    if (!dealData) return [];
    const allImages = [];
    // Add primary image first
    if (dealData.image) allImages.push(dealData.image);
    // Add secondary images
    if (Array.isArray(dealData.images) && dealData.images.length > 0) {
      allImages.push(...dealData.images);
    }
    return allImages;
  }, [dealData]);

  // Image carousel navigation
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % getAllImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(
      (prev) => (prev - 1 + getAllImages.length) % getAllImages.length
    );
  };

  const goToImage = (index) => {
    setCurrentImageIndex(index);
  };

  const discountPercentage = useMemo(() => {
    if (!dealData || !dealData.price || !dealData.discounted_price) return 0;
    return Math.round(
      ((dealData.price - dealData.discounted_price) / dealData.price) * 100
    );
  }, [dealData]);

  // Function to parse and structure product description into compact paragraphs
  const parseProductDescription = (description) => {
    if (!description) return [];

    // First, let's analyze the original format and normalize it
    let normalizedDesc = description
      .replace(/\s+/g, " ") // Normalize whitespace
      .trim();

    // Check if the description already has bullet points
    const hasBullets = /[•·\-*]/.test(normalizedDesc);

    let items = [];

    if (hasBullets) {
      // If it has bullet points, split by them
      normalizedDesc = normalizedDesc.replace(/[•·\-*]/g, "•");
      items = normalizedDesc
        .split("•")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    } else {
      // Enhanced splitting patterns for better readability
      const patterns = [
        // Split by major tech components (processors, graphics, etc.)
        /(?=\b(AMD|Intel|Nvidia|GeForce|RTX|GTX|Windows|macOS|Linux)\b)/gi,
        // Split by memory and storage specifications
        /(?=\b\d+\s*(GB|TB)\s*(DDR|RAM|SSD|HDD|Storage)\b)/gi,
        // Split by display specifications
        /(?=\b\d+\s*(Hz|nits|FHD|HD|4K|8K|Display|Screen)\b)/gi,
        // Split by connectivity features
        /(?=\b(Wi-Fi|Bluetooth|USB|LAN|RJ45|HDMI|DisplayPort|Type-C|Type-A)\b)/gi,
        // Split by audio/video features
        /(?=\b(AI|Built-in|speaker|microphone|webcam|camera|audio)\b)/gi,
        // Split by military standards and certifications
        /(?=\b(US MIL|MIL-STD|military|grade|standard|test|method|procedure)\b)/gi,
        // Split by model and color information
        /(?=\b(Model:|Graphite|Black|White|Silver|Gold|Color|Finish)\b)/gi,
      ];

      // Try each pattern to find the best split
      let bestSplit = [normalizedDesc];
      for (const pattern of patterns) {
        const split = normalizedDesc
          .split(pattern)
          .filter((item) => item.trim().length > 0);
        if (
          split.length > 1 &&
          split.every((item) => item.trim().length > 15)
        ) {
          bestSplit = split;
          break;
        }
      }

      items = bestSplit.map((item) => item.trim());
    }

    // Clean up and format each item with better readability
    items = items
      .map((item) => {
        return (
          item
            // Fix common abbreviations and technical terms with proper spacing
            .replace(
              /\b(\d+)\s*(GB|TB|MHz|GHz|Hz|nits|FHD|HD|SSD|DDR|USB|RJ|Wi-Fi|Bluetooth|Windows|AMD|Intel|Nvidia|GeForce|RTX|GTX|RGB|HDMI|DisplayPort|Type-C|Type-A|MIL-STD|LAN|speaker|microphone|array|port|support|Model|Graphite|Black|White|Silver|Gold)\b/g,
              "$1 $2"
            )
            // Fix multiplication symbols
            .replace(/\b(\d+)\s*x\s*(\d+)\b/g, "$1×$2")
            // Fix ranges
            .replace(/\b(\d+)\s*-\s*(\d+)\b/g, "$1-$2")
            // Fix military standards
            .replace(/\bUS\s+MIL\s+STD\b/gi, "US MIL-STD")
            .replace(/\bMIL\s+STD\b/gi, "MIL-STD")
            // Fix Wi-Fi
            .replace(/\bWi\s*Fi\b/gi, "Wi-Fi")
            // Fix processor specifications
            .replace(/\b(\d+)\s*(cores|threads|Threads)\b/g, "$1 $2")
            .replace(/\b(\d+)\s*(Cache|cache)\b/g, "$1 $2")
            // Fix display specifications
            .replace(/\b(\d+)\s*(Hz|nits)\b/g, "$1 $2")
            .replace(/\b(\d+)\s*x\s*(\d+)\s*(Display|display)\b/g, "$1×$2 $3")
            // Fix connectivity
            .replace(
              /\b(\d+)\s*x\s*(USB|LAN|RJ|speaker|microphone)\b/g,
              "$1×$2"
            )
            .replace(/\b(\d+)\s*-\s*(speaker|microphone|zone|Zone)\b/g, "$1-$2")
            // Clean up extra spaces
            .replace(/\s+/g, " ")
            .trim()
        );
      })
      .filter((item) => item.length > 10); // Filter out very short items

    // Post-process to merge items that seem to be split incorrectly
    const mergedItems = [];
    for (let i = 0; i < items.length; i++) {
      const current = items[i];
      const next = items[i + 1];

      // Enhanced detection of incomplete items
      const isIncomplete =
        // Very short items
        current.length < 25 ||
        // Items ending with incomplete technical terms
        (current.match(
          /\b(standard|grade|test|method|procedure|technology|system|array|port|support|Model|Black|Card|technology|system|array|port|support|Model|Black|Card|Display|display|Sync|sync|Processor|processor|Cache|cache|Threads|threads|cores|GHz|MHz|Hz|nits|speaker|microphone|LAN|USB|Type|band|rigorous|punishing|military|MIL-STD|810H|Wi-Fi|Bluetooth|Dual|2x|3x|1x|AUFA|Graphite|Black|White|Silver|Gold|Color|Finish)\b$/i
        ) &&
          next) ||
        // Items ending with numbers followed by incomplete units
        (current.match(
          /\d+\s*(test|method|procedure|speaker|microphone|LAN|USB|Type|band|rigorous|punishing|cores|threads|Cache|cache|GHz|MHz|Hz|nits|Display|display|Sync|sync|Processor|processor)\b$/i
        ) &&
          next) ||
        // Items ending with incomplete model numbers or colors
        (current.match(
          /\b(AUFA|Model|Graphite|Black|White|Silver|Gold|Color|Finish|standard|grade|test|method|procedure|technology|system|array|port|support|Card|technology|system|array|port|support|Card)\b$/i
        ) &&
          next);

      if (isIncomplete && next) {
        // Merge with next item
        mergedItems.push((current + " " + next).trim());
        i++; // Skip next item since we merged it
      } else {
        mergedItems.push(current);
      }
    }

    // Group items into compact paragraphs (2-3 items per paragraph)
    const paragraphs = [];
    for (let i = 0; i < mergedItems.length; i += 2) {
      const paragraphItems = mergedItems.slice(i, i + 2);
      paragraphs.push(paragraphItems.join(" • "));
    }

    return paragraphs;
  };

  const handleImageZoom = () => setShowImageLightbox(true);
  const closeLightbox = () => {
    setShowImageLightbox(false);
    setCurrentImageIndex(0); // Reset to first image when closing
  };

  // Function to format expiry date
  const formatExpiryDate = (dateString) => {
    if (!dateString || dateString === "N/A") return "No expiry";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      return "No expiry";
    }
  };

  const handleApplicationClick = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    
    // Check if user is active
    const isActive = user?.is_active === 1 || user?.is_active === true;
    if (!isActive) {
      toast.error("Your account must be activated to generate shareable links. Please complete your activation.");
      navigate("/profile");
      return;
    }
    
    setShowConfirmationModal(true);
  };

  const handleApplicationConfirm = async () => {
    setIsSubmitting(true);

    try {
      const payload = {
        affiliate_code: user?.affiliate_code,
        deal_slug: dealData.deal_slug,
      };

      const response = await promotionService.applyPromotion(payload);
      if (response?.data?.status_code === "001") {
        toast.success(
          response?.data?.reason || "Promotion applied successfully"
        );
        setShowConfirmationModal(false);
      } else if (response?.data?.status_code === "003") {
        toast.error(response?.data?.reason || "Deal is already applied");
        setShowConfirmationModal(false);
      } else {
        setShowConfirmationModal(false);
        toast.error(response?.data?.reason || "Failed to apply promotion");
      }
    } catch (error) {
      toast.error(error?.response?.data?.reason || "Failed to apply promotion");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  // Add null check for dealData
  if (!dealData) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <h2 className="mb-4 text-2xl font-bold text-gray-900">
            Deal Not Found
          </h2>
          <p className="mb-6 text-gray-600">
            The deal you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-2 bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-300"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {user ? <AuthenticatedNavbar /> : <TopNavbar />}
      <div className="min-h-screen bg-gray-50">
        {/* Back Button */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-4 py-3 mx-auto mt-16 max-w-8xl sm:px-6 lg:px-8">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 hover:text-[#F2640F] transition-colors font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Deals
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-4 pt-16 pb-8 mx-auto max-w-8xl sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
            {/* Left Column - Product Images */}
            <div className="space-y-3 lg:space-y-4">
              {/* Main Product Image with Carousel */}
              <div className="overflow-hidden relative bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="flex items-center justify-center max-h-[500px] min-h-[300px] bg-gradient-to-br from-gray-50 to-white">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentImageIndex}
                      src={getAllImages[currentImageIndex] || dealData.image}
                      alt={dealData.title}
                      className="w-full h-auto max-h-[500px] object-contain transition-transform duration-300 cursor-pointer hover:scale-105 p-4"
                      onClick={handleImageZoom}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3 }}
                    />
                  </AnimatePresence>

                  {/* Discount Badge */}
                  {discountPercentage > 0 && (
                    <div className="absolute top-4 left-4 z-10 px-3 py-1 text-sm font-bold text-white bg-red-500 rounded-lg">
                      -{discountPercentage}% OFF
                    </div>
                  )}

                  {/* Carousel Navigation - Only show if multiple images */}
                  {getAllImages.length > 1 && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          prevImage();
                        }}
                        className="absolute left-3 top-1/2 z-10 p-2 text-white rounded-full backdrop-blur-sm transition-all duration-200 -translate-y-1/2 bg-black/50 hover:bg-black/70 hover:scale-110"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          nextImage();
                        }}
                        className="absolute right-3 top-1/2 z-10 p-2 text-white rounded-full backdrop-blur-sm transition-all duration-200 -translate-y-1/2 bg-black/50 hover:bg-black/70 hover:scale-110"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>

                      {/* Image Indicators */}
                      <div className="flex absolute bottom-4 left-1/2 z-10 gap-2 -translate-x-1/2">
                        {getAllImages.map((_, index) => (
                          <button
                            key={index}
                            onClick={(e) => {
                              e.stopPropagation();
                              goToImage(index);
                            }}
                            className={`w-2 h-2 rounded-full transition-all duration-200 ${
                              index === currentImageIndex
                                ? "bg-white w-8"
                                : "bg-white/50 hover:bg-white/70"
                            }`}
                          />
                        ))}
                      </div>
                    </>
                  )}

                  {/* Image Counter - Only show if multiple images */}
                  {getAllImages.length > 1 && (
                    <div className="absolute top-3 right-3 z-10 px-3 py-1.5 bg-black/60 backdrop-blur-md text-white rounded-full text-xs font-medium">
                      {currentImageIndex + 1} / {getAllImages.length}
                    </div>
                  )}
                </div>
              </div>

              {/* Thumbnail Strip - Only show if multiple images */}
              {getAllImages.length > 1 && (
                <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
                  {getAllImages.map((img, index) => (
                    <motion.button
                      key={index}
                      onClick={(e) => {
                        e.stopPropagation();
                        goToImage(index);
                      }}
                      className={`flex-shrink-0 relative overflow-hidden rounded-lg border-2 transition-all duration-200 ${
                        index === currentImageIndex
                          ? "border-[#F2640F] shadow-lg scale-105"
                          : "border-transparent hover:border-[#F2640F]/50"
                      }`}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <img
                        src={img}
                        alt={`Thumbnail ${index + 1}`}
                        className="object-cover w-16 h-16 lg:w-20 lg:h-20"
                      />
                      {index === currentImageIndex && (
                        <motion.div
                          className="absolute inset-0 bg-[#F2640F]/20 border-2 border-[#F2640F]"
                          layoutId="activeThumb"
                          transition={{
                            type: "spring",
                            bounce: 0.2,
                            duration: 0.6,
                          }}
                        />
                      )}
                    </motion.button>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column - Product Details */}
            <div className="space-y-6">
              {/* Product Info */}
              <div>
                {/* Brand */}
                <div className="flex gap-2 items-center mb-2">
                  <img
                    src={dealData.company_logo}
                    alt={dealData.company_name}
                    className="object-contain w-6 h-6"
                  />
                  <span className="text-sm text-gray-600">
                    {dealData.company_name}
                  </span>
                </div>

                {/* Title */}
                <h1 className="mb-4 text-3xl font-bold text-gray-900">
                  {dealData.title}
                </h1>

                {/* Rating */}
                <div className="flex gap-2 items-center mb-4">
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className="w-4 h-4 text-yellow-400 fill-current"
                      />
                    ))}
                  </div>
                  <span className="text-sm text-gray-600">
                    (4.8) • 127 reviews
                  </span>
                </div>

                {/* Dynamic Pricing based on Offer Type */}
                {(() => {
                  // Helper to check if BOGO has valid data
                  const hasBogoData = (bogo) => {
                    if (!bogo) return false;
                    const bogoData = Array.isArray(bogo) ? bogo[0] : bogo;
                    return (
                      bogoData &&
                      (bogoData.bogo_item_price ||
                        bogoData.item_price ||
                        (bogoData.bogo_quantity && bogoData.bogo_free_quantity))
                    );
                  };

                  // Helper to check if Bundle has valid data
                  const hasBundleData = (bundle) => {
                    if (!bundle) return false;
                    const bundleData = Array.isArray(bundle)
                      ? bundle[0]
                      : bundle;
                    return (
                      bundleData &&
                      bundleData.original_sum &&
                      bundleData.bundle_price
                    );
                  };

                  // Determine offer type (default to 'discount' if null)
                  const actualOfferType =
                    dealData.offer_type ||
                    (hasBundleData(dealData.bundle)
                      ? "bundle"
                      : hasBogoData(dealData.bogo)
                      ? "bogo"
                      : dealData.discounted_price &&
                        dealData.discounted_price !== 0 &&
                        dealData.price &&
                        dealData.price !== 0
                      ? "discount"
                      : "standard");

                  // BOGO Offer Pricing
                  if (
                    actualOfferType === "bogo" &&
                    hasBogoData(dealData.bogo)
                  ) {
                    const bogoData = Array.isArray(dealData.bogo)
                      ? dealData.bogo[0]
                      : dealData.bogo;
                    return (
                      <div className="overflow-hidden relative p-4 mb-6 bg-gradient-to-br from-blue-50 via-green-50 to-emerald-50 rounded-xl border border-blue-200 shadow-md">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br rounded-full blur-xl from-blue-100/30 to-green-100/30"></div>
                        <div className="relative">
                          <div className="flex gap-2 items-center mb-3">
                            <div className="p-2 bg-gradient-to-r from-blue-500 to-green-500 rounded-lg">
                              <Package className="w-4 h-4 text-white" />
                            </div>
                            <h4 className="text-sm font-bold text-blue-700">
                              Buy One Get One Offer
                            </h4>
                          </div>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="p-3 bg-white rounded-lg border border-blue-100">
                              <p className="mb-1 text-xs text-gray-500">
                                Item Price
                              </p>
                              <p className="text-xl font-bold text-blue-600">
                                GH₵
                                {parseFloat(
                                  bogoData.bogo_item_price ||
                                    bogoData.item_price ||
                                    0
                                ).toFixed(2)}
                              </p>
                            </div>
                            {(bogoData.bogo_quantity || bogoData.quantity) &&
                              (bogoData.bogo_free_quantity ||
                                bogoData.free_quantity) && (
                                <div className="p-3 bg-white rounded-lg border border-green-100">
                                  <p className="mb-1 text-xs text-gray-500">
                                    Deal
                                  </p>
                                  <p className="text-sm font-bold text-green-600">
                                    Buy{" "}
                                    {bogoData.bogo_quantity ||
                                      bogoData.quantity}{" "}
                                    Get{" "}
                                    {bogoData.bogo_free_quantity ||
                                      bogoData.free_quantity}{" "}
                                    Free
                                  </p>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Bundle Offer Pricing
                  if (
                    actualOfferType === "bundle" &&
                    hasBundleData(dealData.bundle)
                  ) {
                    const bundleData = Array.isArray(dealData.bundle)
                      ? dealData.bundle[0]
                      : dealData.bundle;
                    return (
                      <div className="overflow-hidden relative p-4 mb-6 bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 rounded-xl border border-purple-200 shadow-md">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br rounded-full blur-xl from-purple-100/30 to-pink-100/30"></div>
                        <div className="relative">
                          <div className="flex gap-2 items-center mb-3">
                            <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg">
                              <Package className="w-4 h-4 text-white" />
                            </div>
                            <h4 className="text-sm font-bold text-purple-700">
                              Bundle Offer
                            </h4>
                          </div>
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            {bundleData.original_sum && (
                              <div className="p-3 bg-white rounded-lg border border-gray-100">
                                <p className="mb-1 text-xs text-gray-500">
                                  Original Sum
                                </p>
                                <p className="text-lg font-bold text-gray-400 line-through">
                                  GH₵
                                  {parseFloat(bundleData.original_sum).toFixed(
                                    2
                                  )}
                                </p>
                              </div>
                            )}
                            {bundleData.bundle_price && (
                              <div className="p-3 bg-white rounded-lg border border-purple-100">
                                <p className="mb-1 text-xs text-gray-500">
                                  Bundle Price
                                </p>
                                <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                                  GH₵
                                  {parseFloat(bundleData.bundle_price).toFixed(
                                    2
                                  )}
                                </p>
                              </div>
                            )}
                            {bundleData.bundle_savings && (
                              <div className="p-3 bg-white rounded-lg border border-green-100">
                                <p className="mb-1 text-xs text-gray-500">
                                  You Save
                                </p>
                                <p className="text-xl font-bold text-green-600">
                                  GH₵
                                  {parseFloat(
                                    bundleData.bundle_savings
                                  ).toFixed(2)}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Regular Discount Pricing (default)
                  if (
                    (actualOfferType === "discount" ||
                      actualOfferType === "standard") &&
                    dealData.discounted_price !== null &&
                    dealData.discounted_price !== 0 &&
                    dealData.price !== null &&
                    dealData.price !== 0
                  ) {
                    return (
                      <div className="mb-6 relative overflow-hidden bg-gradient-to-br from-[#F2640F]/5 via-orange-50 to-pink-50 p-4 rounded-xl border border-[#F2640F]/20 shadow-md">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#F2640F]/10 to-pink-200/10 rounded-full blur-xl"></div>
                        <div className="relative space-y-3">
                          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-end sm:gap-4">
                            <div className="flex justify-between items-center sm:flex-col sm:items-start sm:space-y-1">
                              <div className="sm:space-y-1">
                                <p className="text-xs font-medium text-gray-500">
                                  Original Price
                                </p>
                                <p className="text-lg font-light text-gray-400 line-through sm:text-xl">
                                  GH₵ {dealData.price?.toLocaleString()}
                                </p>
                              </div>
                              {discountPercentage > 0 && (
                                <div className="sm:hidden">
                                  <span className="bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-md">
                                    {discountPercentage}% OFF
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="text-center sm:text-right">
                              <p className="mb-1 text-xs font-medium text-gray-500">
                                Deal Price
                              </p>
                              <div className="flex gap-2 justify-center items-center sm:justify-end sm:gap-3">
                                <p className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#F2640F] to-[#FF0000] bg-clip-text text-transparent">
                                  GH₵{" "}
                                  {dealData.discounted_price?.toLocaleString()}
                                </p>
                                {discountPercentage > 0 && (
                                  <span className="hidden sm:inline-block bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-md">
                                    {discountPercentage}% OFF
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="p-3 rounded-lg border backdrop-blur-sm bg-white/60 border-white/80">
                            <div className="flex gap-2 justify-center items-center">
                              <span className="text-lg">💰</span>
                              <p className="text-sm text-center text-gray-700 sm:text-base">
                                You save{" "}
                                <span className="font-bold text-[#F2640F] text-lg">
                                  GH₵{" "}
                                  {(
                                    parseFloat(dealData.price) -
                                    parseFloat(dealData.discounted_price)
                                  ).toFixed(2)}
                                </span>{" "}
                                with this deal!
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Fallback for standard pricing
                  return (
                    <div className="mb-6">
                      <div className="flex gap-3 items-center mb-2">
                        <span className="text-3xl font-bold text-gray-900">
                          GH₵{dealData.discounted_price || dealData.price}
                        </span>
                        {dealData.discounted_price && (
                          <span className="text-xl text-gray-500 line-through">
                            GH₵{dealData.price}
                          </span>
                        )}
                      </div>
                      {discountPercentage > 0 && (
                        <div className="flex gap-2 items-center">
                          <span className="px-2 py-1 text-sm font-medium text-red-600 bg-red-100 rounded">
                            Save GH₵
                            {(
                              dealData.price - dealData.discounted_price
                            ).toFixed(2)}
                          </span>
                          <span className="text-sm text-gray-600">
                            ({discountPercentage}% off)
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Flash Sale Countdown - Compact */}
                {dealData.validity_type === "flash_sale" &&
                  dealData.flash &&
                  Array.isArray(dealData.flash) &&
                  dealData.flash.length > 0 && (
                    <div className="overflow-hidden relative p-4 mb-6 bg-gradient-to-r from-red-50 via-orange-50 to-yellow-50 rounded-xl border-2 border-red-400 animate-pulse">
                      <div className="absolute top-0 left-0 w-16 h-16 rounded-full blur-xl animate-pulse bg-red-500/10"></div>
                      <div className="relative">
                        {/* Horizontal Layout: Heading Left, Countdown Right */}
                        <div className="flex flex-col gap-4 items-start md:flex-row md:items-center md:justify-between">
                          {/* Left Side - Header */}
                          <div className="flex gap-3 items-center">
                            <div className="p-2 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg animate-pulse">
                              <Zap className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-red-700">
                                🔥 FLASH SALE - Limited Time Only!
                              </p>
                              {dealData.flash[0]?.flash_end_time && (
                                <p className="text-xs font-semibold text-red-600">
                                  Ends:{" "}
                                  {moment(
                                    dealData.flash[0].flash_end_time
                                  ).format("DD MMM, YYYY HH:mm")}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Right Side - Countdown Timer */}
                          <div className="flex flex-wrap gap-2 md:gap-3">
                            {flashSaleCountdown.days > 0 ||
                            flashSaleCountdown.hours > 0 ||
                            flashSaleCountdown.minutes > 0 ||
                            flashSaleCountdown.seconds > 0 ? (
                              <>
                                {flashSaleCountdown.days > 0 && (
                                  <div className="flex flex-col items-center justify-center p-2 md:p-3 min-w-[50px] md:min-w-[60px] bg-white/95 rounded-xl border-2 border-red-500 shadow-lg">
                                    <p className="text-2xl font-black text-red-600 md:text-3xl">
                                      {String(flashSaleCountdown.days).padStart(
                                        2,
                                        "0"
                                      )}
                                    </p>
                                    <p className="text-xs font-bold tracking-wide text-red-700 uppercase">
                                      Days
                                    </p>
                                  </div>
                                )}
                                <div className="flex flex-col items-center justify-center p-2 md:p-3 min-w-[50px] md:min-w-[60px] bg-white/95 rounded-xl border-2 border-red-500 shadow-lg animate-pulse">
                                  <p className="text-2xl font-black text-red-600 md:text-3xl">
                                    {String(flashSaleCountdown.hours).padStart(
                                      2,
                                      "0"
                                    )}
                                  </p>
                                  <p className="text-xs font-bold tracking-wide text-red-700 uppercase">
                                    Hours
                                  </p>
                                </div>
                                <div
                                  className="flex flex-col items-center justify-center p-2 md:p-3 min-w-[50px] md:min-w-[60px] bg-white/95 rounded-xl border-2 border-red-500 shadow-lg animate-pulse"
                                  style={{ animationDelay: "0.2s" }}
                                >
                                  <p className="text-2xl font-black text-red-600 md:text-3xl">
                                    {String(
                                      flashSaleCountdown.minutes
                                    ).padStart(2, "0")}
                                  </p>
                                  <p className="text-xs font-bold tracking-wide text-red-700 uppercase">
                                    Mins
                                  </p>
                                </div>
                                <div
                                  className="flex flex-col items-center justify-center p-2 md:p-3 min-w-[50px] md:min-w-[60px] bg-white/95 rounded-xl border-2 border-red-500 shadow-lg animate-pulse"
                                  style={{ animationDelay: "0.4s" }}
                                >
                                  <p className="text-2xl font-black text-red-600 md:text-3xl">
                                    {String(
                                      flashSaleCountdown.seconds
                                    ).padStart(2, "0")}
                                  </p>
                                  <p className="text-xs font-bold tracking-wide text-red-700 uppercase">
                                    Secs
                                  </p>
                                </div>
                              </>
                            ) : (
                              <div className="p-3 bg-red-600 rounded-xl border-2 border-red-500 md:p-4">
                                <p className="text-base font-bold text-white uppercase md:text-lg">
                                  Sale Expired
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
              </div>

              {/* Purchase Section */}
              <div className="p-6 bg-white rounded-xl border border-gray-200">
                {/* Action Buttons */}
                <div className="space-y-3">
                  {/* Generate Shareable Link Button */}
                  {(() => {
                    const isActive = user?.is_active === 1 || user?.is_active === true;
                    const canPromote = user && isActive;
                    
                    return (
                      <button
                        onClick={handleApplicationClick}
                        disabled={!canPromote}
                        className={`w-full flex items-center justify-center gap-2 px-6 py-3 font-semibold rounded-lg transition-all duration-200 ${
                          canPromote
                            ? "bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white hover:shadow-lg"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                        }`}
                        title={!user ? "Please login to generate shareable links" : !isActive ? "Account must be activated to generate shareable links" : ""}
                      >
                        <Share2 className="w-5 h-5" />
                        {!user 
                          ? "Login to Get Shareable Link" 
                          : !isActive 
                          ? "Activation Required" 
                          : "Apply for Shareable Link"}
                      </button>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

          {/* Product Details Section */}
          <div className="mt-12">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              {/* Product Information */}
              <div className="space-y-8 lg:col-span-2">
                {/* Description - Only show for old data format (when no properties exist) */}
                {(!dealData.properties || dealData.properties.length === 0) &&
                  (dealData.details?.description || dealData.description) && (
                    <div className="p-6 bg-white rounded-xl border border-gray-200">
                      <h2 className="mb-4 text-xl font-semibold text-gray-900">
                        Product Description
                      </h2>
                      <div className="space-y-3">
                        {parseProductDescription(
                          dealData.details?.description || dealData.description
                        ).length > 0 ? (
                          parseProductDescription(
                            dealData.details?.description ||
                              dealData.description
                          ).map((paragraph, index) => (
                            <p
                              key={index}
                              className="text-sm leading-relaxed text-gray-700"
                            >
                              {paragraph}
                            </p>
                          ))
                        ) : (
                          <p className="leading-relaxed text-gray-700">
                            {dealData.details?.description ||
                              dealData.description ||
                              "No description available"}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                {/* Properties */}
                {dealData.properties &&
                  Array.isArray(dealData.properties) &&
                  dealData.properties.length > 0 && (
                    <div className="p-6 bg-white rounded-xl border border-gray-200">
                      <div className="flex gap-3 items-center mb-4">
                        <div className="p-2 bg-gradient-to-r to-gray-600 rounded-lg from-slate-500">
                          <Package className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-xl font-semibold text-gray-900">
                          Product Properties
                        </h2>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {dealData.properties.map((property, index) => (
                          <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="overflow-hidden relative p-4 bg-gradient-to-br to-gray-50 rounded-lg border transition-all duration-300 from-slate-50 border-slate-200 hover:border-slate-300 hover:shadow-md group"
                          >
                            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br to-gray-100 rounded-full opacity-0 blur-2xl transition-opacity duration-300 from-slate-100 group-hover:opacity-100"></div>
                            <div className="relative">
                              <div className="flex gap-2 items-center mb-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-slate-500 to-gray-600"></div>
                                <span className="text-xs font-semibold tracking-wide uppercase text-slate-500">
                                  {property.name}
                                </span>
                              </div>
                              <p className="text-base font-bold text-slate-900">
                                {property.value}
                              </p>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Flash Sale Information */}
                {dealData.validity_type === "flash_sale" &&
                  dealData.flash &&
                  Array.isArray(dealData.flash) &&
                  dealData.flash.length > 0 && (
                    <div className="overflow-hidden relative p-6 bg-gradient-to-r from-red-50 via-orange-50 to-yellow-50 rounded-2xl border-2 border-red-400 shadow-lg">
                      <div className="absolute top-0 left-0 w-32 h-32 rounded-full blur-2xl animate-pulse bg-red-500/20"></div>
                      <div className="absolute right-0 bottom-0 w-24 h-24 rounded-full blur-xl animate-pulse bg-orange-500/10"></div>

                      <div className="flex relative gap-4 items-center mb-6">
                        <div className="p-3 bg-gradient-to-r from-red-500 to-orange-500 rounded-lg animate-pulse">
                          <Zap className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-red-700">
                            🔥 Flash Sale LIVE!
                          </h3>
                          <p className="text-sm text-red-600">
                            Limited time offer - Act fast!
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        {/* Left Side - Flash Sale Details */}
                        <div className="space-y-3">
                          <h4 className="text-sm font-semibold tracking-wide text-red-700 uppercase">
                            Sale Schedule
                          </h4>
                          {dealData.flash.map((flashItem, index) => (
                            <div key={index} className="space-y-3">
                              <div className="p-3 rounded-lg border-2 border-red-200 shadow-sm bg-white/95">
                                <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                                  Start Time
                                </span>
                                <div className="text-base font-bold text-red-600">
                                  {moment(flashItem.flash_start_time).format(
                                    "DD MMMM YYYY, HH:mm"
                                  )}
                                </div>
                              </div>
                              <div className="p-3 rounded-lg border-2 border-red-200 shadow-sm bg-white/95">
                                <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                                  End Time
                                </span>
                                <div className="text-base font-bold text-red-600">
                                  {moment(flashItem.flash_end_time).format(
                                    "DD MMMM YYYY, HH:mm"
                                  )}
                                </div>
                              </div>
                              {flashItem.flash_duration && (
                                <div className="p-3 rounded-lg border-2 border-orange-200 shadow-sm bg-white/95">
                                  <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">
                                    Duration
                                  </span>
                                  <div className="text-base font-bold text-orange-600">
                                    {flashItem.flash_duration} hours
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Right Side - Live Countdown Timer */}
                        <div className="flex flex-col justify-center items-center lg:items-end lg:justify-center">
                          <div className="mb-3">
                            <p className="text-xs font-bold tracking-wider text-center text-red-700 uppercase lg:text-right">
                              Time Remaining
                            </p>
                          </div>
                          <div className="flex gap-3">
                            {flashSaleCountdown.days > 0 ||
                            flashSaleCountdown.hours > 0 ||
                            flashSaleCountdown.minutes > 0 ||
                            flashSaleCountdown.seconds > 0 ? (
                              <>
                                {flashSaleCountdown.days > 0 && (
                                  <div className="flex flex-col items-center p-3 min-w-[70px] bg-white/95 rounded-xl border-2 border-red-500 shadow-lg">
                                    <p className="text-3xl font-black text-red-600">
                                      {String(flashSaleCountdown.days).padStart(
                                        2,
                                        "0"
                                      )}
                                    </p>
                                    <p className="text-xs font-bold tracking-wide text-red-700 uppercase">
                                      Days
                                    </p>
                                  </div>
                                )}
                                <div className="flex flex-col items-center p-3 min-w-[70px] bg-white/95 rounded-xl border-2 border-red-500 shadow-lg animate-pulse">
                                  <p className="text-3xl font-black text-red-600">
                                    {String(flashSaleCountdown.hours).padStart(
                                      2,
                                      "0"
                                    )}
                                  </p>
                                  <p className="text-xs font-bold tracking-wide text-red-700 uppercase">
                                    Hours
                                  </p>
                                </div>
                                <div
                                  className="flex flex-col items-center p-3 min-w-[70px] bg-white/95 rounded-xl border-2 border-red-500 shadow-lg animate-pulse"
                                  style={{ animationDelay: "0.2s" }}
                                >
                                  <p className="text-3xl font-black text-red-600">
                                    {String(
                                      flashSaleCountdown.minutes
                                    ).padStart(2, "0")}
                                  </p>
                                  <p className="text-xs font-bold tracking-wide text-red-700 uppercase">
                                    Mins
                                  </p>
                                </div>
                                <div
                                  className="flex flex-col items-center p-3 min-w-[70px] bg-white/95 rounded-xl border-2 border-red-500 shadow-lg animate-pulse"
                                  style={{ animationDelay: "0.4s" }}
                                >
                                  <p className="text-3xl font-black text-red-600">
                                    {String(
                                      flashSaleCountdown.seconds
                                    ).padStart(2, "0")}
                                  </p>
                                  <p className="text-xs font-bold tracking-wide text-red-700 uppercase">
                                    Secs
                                  </p>
                                </div>
                              </>
                            ) : (
                              <div className="p-6 bg-red-600 rounded-xl border-2 border-red-500">
                                <p className="text-xl font-black text-white uppercase">
                                  Sale Expired
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Specifications */}
                <div className="p-6 bg-white rounded-xl border border-gray-200">
                  <h2 className="mb-4 text-xl font-semibold text-gray-900">
                    Specifications
                  </h2>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Brand</span>
                      <span className="font-medium">
                        {dealData.company_name}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Category</span>
                      <span className="font-medium">
                        {dealData.category?.[0] || "N/A"}
                      </span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Status</span>
                      <span className="font-medium">{dealData.status}</span>
                    </div>
                    <div className="flex justify-between py-2 border-b border-gray-100">
                      <span className="text-gray-600">Expiry</span>
                      <span className="font-medium">
                        {formatExpiryDate(dealData.expiry_date)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Terms & Conditions */}
                {dealData.terms && (
                  <div className="p-6 bg-white rounded-xl border border-gray-200">
                    <h2 className="mb-4 text-xl font-semibold text-gray-900">
                      Terms & Conditions
                    </h2>

                    {(() => {
                      // Check if this is new format (structured terms with sections)
                      const isNewFormat =
                        dealData.terms &&
                        (dealData.terms.includes("Deal Validity:") ||
                          dealData.terms.includes("Redemption Location:") ||
                          dealData.terms.includes("Offer Details:") ||
                          dealData.terms.includes("Redemption Process:") ||
                          dealData.terms.includes("Terms and Limitations:") ||
                          dealData.terms.includes("Privacy and Data:"));

                      // For new format, show beautifully structured content
                      if (isNewFormat) {
                        const parseStructuredTerms = (text) => {
                          const sections = [];
                          const lines = text.split("\n");
                          let currentSection = null;
                          let currentContent = [];

                          for (let i = 0; i < lines.length; i++) {
                            const line = lines[i].trim();
                            if (!line) continue;

                            // Check if this is a section header
                            if (
                              line.endsWith(":") &&
                              line.length < 100 &&
                              !line.startsWith("-") &&
                              !line.startsWith("•") &&
                              line.split(" ").length < 5
                            ) {
                              // Save previous section if exists
                              if (currentSection) {
                                sections.push({
                                  title: currentSection,
                                  content: currentContent,
                                });
                              }
                              // Start new section
                              currentSection = line;
                              currentContent = [];
                            } else if (currentSection) {
                              currentContent.push(line);
                            } else {
                              // This might be the introduction
                              if (sections.length === 0) {
                                sections.push({
                                  title: "Overview",
                                  content: [line],
                                });
                              } else {
                                currentContent.push(line);
                              }
                            }
                          }

                          // Add last section
                          if (currentSection) {
                            sections.push({
                              title: currentSection,
                              content: currentContent,
                            });
                          }

                          return sections;
                        };

                        const sections = parseStructuredTerms(dealData.terms);

                        return (
                          <div className="space-y-4">
                            {sections
                              .filter((section) => {
                                // Filter out sections with no content, especially "Redemption Process:"
                                return (
                                  section.content &&
                                  section.content.length > 0 &&
                                  section.content.some((item) => item.trim())
                                );
                              })
                              .map((section, index) => {
                                const iconMap = {
                                  "Vendor Information:": {
                                    icon: "🏢",
                                    color: "blue",
                                  },
                                  "Deal Validity:": {
                                    icon: "⏰",
                                    color: "orange",
                                  },
                                  "Redemption Location:": {
                                    icon: "📍",
                                    color: "green",
                                  },
                                  "Offer Details:": {
                                    icon: "🎁",
                                    color: "purple",
                                  },
                                  "Redemption Process:": {
                                    icon: "⚙️",
                                    color: "indigo",
                                  },
                                  "Important Notes:": {
                                    icon: "⚠️",
                                    color: "amber",
                                  },
                                  "Terms and Limitations:": {
                                    icon: "📋",
                                    color: "slate",
                                  },
                                  "Misuse and Fraud:": {
                                    icon: "🚫",
                                    color: "red",
                                  },
                                  "Privacy and Data:": {
                                    icon: "🔒",
                                    color: "blue",
                                  },
                                  "Governing Law:": {
                                    icon: "⚖️",
                                    color: "purple",
                                  },
                                  "Category Specific Terms:": {
                                    icon: "🏷️",
                                    color: "teal",
                                  },
                                  "Delivery Information:": {
                                    icon: "🚚",
                                    color: "green",
                                  },
                                };

                                const config = iconMap[section.title] || {
                                  icon: "📄",
                                  color: "gray",
                                };
                                const colorClasses = {
                                  blue: "from-blue-50 to-blue-100 border-blue-200 text-blue-700",
                                  orange:
                                    "from-orange-50 to-orange-100 border-orange-200 text-orange-700",
                                  green:
                                    "from-green-50 to-green-100 border-green-200 text-green-700",
                                  purple:
                                    "from-purple-50 to-purple-100 border-purple-200 text-purple-700",
                                  indigo:
                                    "from-indigo-50 to-indigo-100 border-indigo-200 text-indigo-700",
                                  amber:
                                    "from-amber-50 to-amber-100 border-amber-200 text-amber-700",
                                  slate:
                                    "from-slate-50 to-slate-100 border-slate-200 text-slate-700",
                                  red: "from-red-50 to-red-100 border-red-200 text-red-700",
                                  teal: "from-teal-50 to-teal-100 border-teal-200 text-teal-700",
                                };

                                return (
                                  <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className={`p-5 bg-gradient-to-r ${
                                      colorClasses[config.color]
                                    } rounded-xl border-2 shadow-sm`}
                                  >
                                    <div className="flex gap-3 items-center mb-3">
                                      <span className="text-2xl">
                                        {config.icon}
                                      </span>
                                      <h4 className="text-lg font-bold">
                                        {section.title}
                                      </h4>
                                    </div>
                                    <div className="pl-4 space-y-2">
                                      {section.content.map((item, idx) => (
                                        <p
                                          key={idx}
                                          className="text-sm leading-relaxed"
                                        >
                                          {item.startsWith("•")
                                            ? item
                                            : `• ${item}`}
                                        </p>
                                      ))}
                                    </div>
                                  </motion.div>
                                );
                              })}
                          </div>
                        );
                      }

                      // For old format, show simple text
                      return (
                        <div className="max-w-none prose prose-gray">
                          <p className="leading-relaxed text-gray-700 whitespace-pre-line">
                            {dealData.terms}
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Store Information */}
                <div className="p-6 bg-white rounded-xl border border-gray-200">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">
                    Store Information
                  </h3>
                  <div className="flex gap-3 items-center mb-4">
                    <img
                      src={dealData.company_logo}
                      alt={dealData.company_name}
                      className="object-contain w-12 h-12 rounded-lg border border-gray-200"
                    />
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {dealData.company_name}
                      </h4>
                      <p className="text-sm text-gray-600">Verified Store</p>
                    </div>
                  </div>

                  {/* Contact Info */}
                  {dealData.contact && (
                    <div className="space-y-2">
                      {dealData.contact.email && (
                        <div className="flex gap-2 items-center text-sm text-gray-600">
                          <Mail className="w-4 h-4" />
                          <span>{dealData.contact.email}</span>
                        </div>
                      )}
                      {dealData.contact.phone && (
                        <div className="flex gap-2 items-center text-sm text-gray-600">
                          <Phone className="w-4 h-4" />
                          <span>{dealData.contact.phone}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Location */}
                {dealData.google_map_url && (
                  <div className="p-6 bg-white rounded-xl border border-gray-200">
                    <h3 className="mb-4 text-lg font-semibold text-gray-900">
                      Location
                    </h3>
                    <a
                      href={dealData.google_map_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-[#F2640F] hover:text-[#FF0000] transition-colors"
                    >
                      <MapPin className="w-4 h-4" />
                      <span>View on Google Maps</span>
                    </a>
                  </div>
                )}

                {/* Deal Status */}
                <div className="p-6 bg-white rounded-xl border border-gray-200">
                  <h3 className="mb-4 text-lg font-semibold text-gray-900">
                    Deal Status
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Status</span>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          dealData.status === "Approved"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {dealData.status === "Approved" ? "Active" : "Inactive"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Expires</span>
                      <span className="text-sm font-medium">
                        {formatExpiryDate(dealData.expiry_date)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Redemption Locations - Compact */}
                {(() => {
                  // Priority 1: redemption_branches
                  if (
                    Array.isArray(dealData.redemption_branches) &&
                    dealData.redemption_branches.length > 0
                  ) {
                    return (
                      <div className="p-6 bg-white rounded-xl border border-gray-200">
                        <h3 className="mb-4 text-lg font-semibold text-gray-900">
                          Redemption Locations
                        </h3>
                        <div className="flex gap-2 items-center mb-3">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                            {dealData.redemption_branches.length}{" "}
                            {dealData.redemption_branches.length === 1
                              ? "Branch"
                              : "Branches"}
                          </span>
                        </div>
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                          {dealData.redemption_branches
                            .slice(0, 5)
                            .map((branch, index) => (
                              <div
                                key={index}
                                className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100"
                              >
                                <h6 className="mb-1 text-sm font-medium text-gray-900">
                                  {branch.name || branch.branch_name}
                                </h6>
                                <div className="flex gap-2 items-center text-xs text-gray-600">
                                  <MapPin className="flex-shrink-0 w-3 h-3 text-green-500" />
                                  <span className="truncate">
                                    {branch.location}
                                  </span>
                                </div>
                                {branch.phone_number && (
                                  <div className="flex gap-2 items-center mt-1 text-xs text-gray-600">
                                    <Phone className="flex-shrink-0 w-3 h-3 text-green-500" />
                                    <a
                                      href={`tel:${branch.phone_number}`}
                                      className="truncate hover:text-green-600"
                                    >
                                      {branch.phone_number}
                                    </a>
                                  </div>
                                )}
                              </div>
                            ))}
                          {dealData.redemption_branches.length > 5 && (
                            <p className="pt-2 text-xs text-center text-gray-500">
                              +{dealData.redemption_branches.length - 5} more
                              locations
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Priority 2: redemption_partners
                  if (
                    Array.isArray(dealData.redemption_partners) &&
                    dealData.redemption_partners.length > 0
                  ) {
                    return (
                      <div className="p-6 bg-white rounded-xl border border-gray-200">
                        <h3 className="mb-4 text-lg font-semibold text-gray-900">
                          Redemption Locations
                        </h3>
                        <div className="space-y-3 max-h-[500px] overflow-y-auto">
                          {dealData.redemption_partners.map(
                            (partner, partnerIndex) => (
                              <div
                                key={partnerIndex}
                                className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100"
                              >
                                <div className="flex gap-2 items-center mb-2">
                                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                  <h5 className="text-sm font-semibold text-green-800">
                                    {partner.partner_name}
                                  </h5>
                                </div>
                                {partner.branches &&
                                  partner.branches.length > 0 && (
                                    <div className="space-y-2">
                                      {partner.branches
                                        .slice(0, 3)
                                        .map((branch, branchIndex) => (
                                          <div
                                            key={branchIndex}
                                            className="pl-4 border-l-2 border-green-200"
                                          >
                                            <h6 className="text-xs font-medium text-gray-900 truncate">
                                              {branch.name}
                                            </h6>
                                            <div className="flex gap-1 items-center text-xs text-gray-600">
                                              <MapPin className="flex-shrink-0 w-3 h-3 text-green-500" />
                                              <span className="truncate">
                                                {branch.location}
                                              </span>
                                            </div>
                                          </div>
                                        ))}
                                      {partner.branches.length > 3 && (
                                        <p className="pt-1 pl-4 text-xs text-gray-500">
                                          +{partner.branches.length - 3} more
                                        </p>
                                      )}
                                    </div>
                                  )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Priority 3: redemption_region_location
                  if (
                    Array.isArray(dealData.redemption_region_location) &&
                    dealData.redemption_region_location.length > 0
                  ) {
                    // Check if it's "all" and "all" for nationwide
                    const isNationwide = dealData.redemption_region_location.length === 1 &&
                      dealData.redemption_region_location[0].location?.toLowerCase() === "all" &&
                      dealData.redemption_region_location[0].region?.toLowerCase() === "all";

                    return (
                      <div className="p-6 bg-white rounded-xl border border-gray-200">
                        <h3 className="mb-4 text-lg font-semibold text-gray-900">
                          Redemption Locations
                        </h3>
                        {isNationwide ? (
                          <div className="p-6 text-center bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-300 shadow-sm">
                            <div className="flex flex-col gap-3 justify-center items-center">
                              <div className="p-4 bg-green-100 rounded-full">
                                <MapPin className="w-6 h-6 text-green-600" />
                              </div>
                              <h4 className="text-lg font-bold text-green-800">Nationwide Coverage</h4>
                              <p className="text-sm text-green-700">
                                Available across all regions and cities in Ghana
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {dealData.redemption_region_location.map(
                              (locale, index) => (
                                <div
                                  key={index}
                                  className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100"
                                >
                                  <h6 className="text-sm font-medium text-gray-900">
                                    {locale.location}
                                  </h6>
                                  <div className="flex gap-1 items-center text-xs text-gray-600">
                                    <MapPin className="flex-shrink-0 w-3 h-3 text-green-500" />
                                    <span>{locale.region}</span>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }

                  return null;
                })()}

                {/* Target Locations - Compact */}
                {(() => {
                  const targetSections = [];

                  // Priority 1: target_branches
                  if (
                    Array.isArray(dealData.target_branches) &&
                    dealData.target_branches.length > 0
                  ) {
                    targetSections.push(
                      <div
                        key="target_branches"
                        className="p-6 bg-white rounded-xl border border-gray-200"
                      >
                        <h3 className="mb-4 text-lg font-semibold text-gray-900">
                          Target Locations
                        </h3>
                        <div className="flex gap-2 items-center mb-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                            {dealData.target_branches.length}{" "}
                            {dealData.target_branches.length === 1
                              ? "Branch"
                              : "Branches"}
                          </span>
                        </div>
                        <div className="space-y-3 max-h-[300px] overflow-y-auto">
                          {dealData.target_branches
                            .slice(0, 4)
                            .map((branch, index) => (
                              <div
                                key={index}
                                className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100"
                              >
                                <h6 className="mb-1 text-sm font-medium text-gray-900">
                                  {branch.name || branch.branch_name}
                                </h6>
                                <div className="flex gap-2 items-center text-xs text-gray-600">
                                  <MapPin className="flex-shrink-0 w-3 h-3 text-blue-500" />
                                  <span className="truncate">
                                    {branch.location}
                                  </span>
                                </div>
                              </div>
                            ))}
                          {dealData.target_branches.length > 4 && (
                            <p className="pt-2 text-xs text-center text-gray-500">
                              +{dealData.target_branches.length - 4} more
                              locations
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Priority 2: target_partners
                  if (
                    Array.isArray(dealData.target_partners) &&
                    dealData.target_partners.length > 0
                  ) {
                    targetSections.push(
                      <div
                        key="target_partners"
                        className="p-6 bg-white rounded-xl border border-gray-200"
                      >
                        <h3 className="mb-4 text-lg font-semibold text-gray-900">
                          Target Locations
                        </h3>
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                          {dealData.target_partners.map(
                            (partner, partnerIndex) => (
                              <div
                                key={partnerIndex}
                                className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100"
                              >
                                <div className="flex gap-2 items-center mb-2">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  <h5 className="text-sm font-semibold text-blue-800">
                                    {partner.partner_name}
                                  </h5>
                                </div>
                                {partner.branches &&
                                  partner.branches.length > 0 && (
                                    <div className="space-y-2">
                                      {partner.branches
                                        .slice(0, 2)
                                        .map((branch, branchIndex) => (
                                          <div
                                            key={branchIndex}
                                            className="pl-4 border-l-2 border-blue-200"
                                          >
                                            <h6 className="text-xs font-medium text-gray-900 truncate">
                                              {branch.name}
                                            </h6>
                                            <div className="flex gap-1 items-center text-xs text-gray-600">
                                              <MapPin className="flex-shrink-0 w-3 h-3 text-blue-500" />
                                              <span className="truncate">
                                                {branch.location}
                                              </span>
                                            </div>
                                          </div>
                                        ))}
                                      {partner.branches.length > 2 && (
                                        <p className="pt-1 pl-4 text-xs text-gray-500">
                                          +{partner.branches.length - 2} more
                                        </p>
                                      )}
                                    </div>
                                  )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Priority 3: partner_location (can be string or array)
                  let partnerLocationData = dealData.partner_location;
                  if (
                    typeof dealData.partner_location === "string" &&
                    dealData.partner_location.trim() !== "" &&
                    dealData.partner_location.trim() !== "[]"
                  ) {
                    try {
                      partnerLocationData = JSON.parse(
                        dealData.partner_location
                      );
                    } catch (error) {
                      partnerLocationData = null;
                    }
                  }

                  if (
                    Array.isArray(partnerLocationData) &&
                    partnerLocationData.length > 0
                  ) {
                    targetSections.push(
                      <div
                        key="partner_location"
                        className="p-6 bg-white rounded-xl border border-gray-200"
                      >
                        <h3 className="mb-4 text-lg font-semibold text-gray-900">
                          Partner Locations
                        </h3>
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                          {partnerLocationData.map((partner, partnerIndex) => (
                            <div
                              key={partnerIndex}
                              className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100"
                            >
                              <div className="flex gap-2 items-center mb-2">
                                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                                <h5 className="text-sm font-semibold text-purple-800">
                                  {partner.partner_name}
                                </h5>
                              </div>
                              {partner.branches &&
                                partner.branches.length > 0 && (
                                  <div className="space-y-2">
                                    {partner.branches
                                      .slice(0, 2)
                                      .map((branch, branchIndex) => (
                                        <div
                                          key={branchIndex}
                                          className="pl-4 border-l-2 border-purple-200"
                                        >
                                          <h6 className="text-xs font-medium text-gray-900 truncate">
                                            {branch.name}
                                          </h6>
                                          <div className="flex gap-1 items-center text-xs text-gray-600">
                                            <MapPin className="flex-shrink-0 w-3 h-3 text-purple-500" />
                                            <span className="truncate">
                                              {branch.location}
                                            </span>
                                          </div>
                                        </div>
                                      ))}
                                    {partner.branches.length > 2 && (
                                      <p className="pt-1 pl-4 text-xs text-gray-500">
                                        +{partner.branches.length - 2} more
                                      </p>
                                    )}
                                  </div>
                                )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  // Priority 4: region_location
                  if (
                    Array.isArray(dealData.region_location) &&
                    dealData.region_location.length > 0
                  ) {
                    // Check if it's "all" and "all" for nationwide
                    const isNationwide = dealData.region_location.length === 1 &&
                      dealData.region_location[0].location?.toLowerCase() === "all" &&
                      dealData.region_location[0].region?.toLowerCase() === "all";

                    targetSections.push(
                      <div
                        key="region_location"
                        className="p-6 bg-white rounded-xl border border-gray-200"
                      >
                        <h3 className="mb-4 text-lg font-semibold text-gray-900">
                          Target Regions
                        </h3>
                        {isNationwide ? (
                          <div className="p-6 text-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-300 shadow-sm">
                            <div className="flex flex-col gap-3 justify-center items-center">
                              <div className="p-4 bg-blue-100 rounded-full">
                                <MapPin className="w-6 h-6 text-blue-600" />
                              </div>
                              <h4 className="text-lg font-bold text-blue-800">Nationwide Coverage</h4>
                              <p className="text-sm text-blue-700">
                                Available across all regions and cities in Ghana
                              </p>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="flex gap-2 items-center mb-3">
                              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                              <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded-full">
                                {dealData.region_location.length}{" "}
                                {dealData.region_location.length === 1
                                  ? "Region"
                                  : "Regions"}
                              </span>
                            </div>
                            <div className="space-y-2">
                              {dealData.region_location.map((locale, index) => (
                                <div
                                  key={index}
                                  className="p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100"
                                >
                                  <h6 className="text-sm font-medium text-gray-900">
                                    {locale.location}
                                  </h6>
                                  <div className="flex gap-1 items-center text-xs text-gray-600">
                                    <MapPin className="flex-shrink-0 w-3 h-3 text-blue-500" />
                                    <span>{locale.region}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  }

                  return targetSections.length > 0 ? targetSections : null;
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Image Lightbox */}
        <AnimatePresence>
          {showImageLightbox && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-90"
              onClick={closeLightbox}
            >
              <div className="relative max-w-4xl max-h-[90vh] p-4">
                <button
                  onClick={closeLightbox}
                  className="absolute top-4 right-4 z-20 p-2 text-white bg-black bg-opacity-50 rounded-full transition-colors hover:bg-opacity-70"
                >
                  <X className="w-6 h-6" />
                </button>

                <div className="flex items-center justify-center max-w-full max-h-[90vh] overflow-hidden px-2">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.img
                      key={currentImageIndex}
                      src={getAllImages[currentImageIndex] || dealData.image}
                      alt={dealData.title}
                      className="max-w-full max-h-[90vh] object-contain rounded-lg"
                      onClick={(e) => e.stopPropagation()}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.3 }}
                    />
                  </AnimatePresence>
                </div>

                {/* Lightbox Carousel Navigation - Only show if multiple images */}
                {getAllImages.length > 1 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        prevImage();
                      }}
                      className="absolute left-4 top-1/2 z-10 p-3 text-white rounded-full backdrop-blur-sm transition-all duration-200 -translate-y-1/2 bg-black/50 hover:bg-black/70 hover:scale-110"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        nextImage();
                      }}
                      className="absolute right-4 top-1/2 z-10 p-3 text-white rounded-full backdrop-blur-sm transition-all duration-200 -translate-y-1/2 bg-black/50 hover:bg-black/70 hover:scale-110"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>

                    {/* Thumbnail Strip in Lightbox */}
                    <div className="flex overflow-x-auto absolute bottom-16 left-1/2 gap-2 px-4 max-w-full -translate-x-1/2">
                      {getAllImages.map((img, index) => (
                        <img
                          key={index}
                          src={img}
                          alt={`Thumbnail ${index + 1}`}
                          className={`w-16 h-16 rounded-lg cursor-pointer object-cover flex-shrink-0 border-2 transition-all ${
                            index === currentImageIndex
                              ? "border-white"
                              : "border-transparent hover:border-white/50"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            goToImage(index);
                          }}
                        />
                      ))}
                    </div>

                    <div className="flex absolute right-0 left-0 bottom-2 justify-center px-2">
                      <div className="px-3 py-2 text-xs text-center text-white rounded-full backdrop-blur-sm bg-black/50">
                        {currentImageIndex + 1} / {getAllImages.length}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Application Confirmation Modal */}
        <ApplicationConfirmationModal
          isOpen={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={handleApplicationConfirm}
          dealData={dealData}
          isLoading={isSubmitting}
        />
      </div>
    </>
  );
};

export default DealDetails;
